import Button from "@/components/Button";
import Dialog from "@/components/Dialog";
import ListItem from "@/components/ListItem";
import { useMutation } from "@apollo/client";
import { DELETE_STUDENT_INTERNSHIP_MUTATION } from "../../../../internships/mutations";
import { STUDENT_INTERNSHIPS_QUERY } from "../../../../internships/queries";
import { showSnackbar } from "@/lib/snackbarUtils";
import { useErrorBanner } from "@/providers/ErrorBannerProvider";

function formatDateRange(startDate?: string, endDate?: string): string | undefined {
  if (!startDate && !endDate) return undefined;
  if (startDate && endDate) return `${startDate} – ${endDate}`;
  return startDate || endDate;
}

interface DeleteInternshipModalProps {
  open: boolean;
  onClose: () => void;
  internshipId: string;
  facilityName?: string;
  startDate?: string;
  endDate?: string;
  refetchVariables: { input: { universityId: string; search: string } };
}

export default function DeleteInternshipModal({
  open,
  onClose,
  internshipId,
  facilityName,
  startDate,
  endDate,
  refetchVariables
}: DeleteInternshipModalProps) {
  const { setError, clearError } = useErrorBanner();
  const [deleteInternship, { loading }] = useMutation(DELETE_STUDENT_INTERNSHIP_MUTATION, {
    refetchQueries: [{ query: STUDENT_INTERNSHIPS_QUERY, variables: refetchVariables }],

    onCompleted: (data) => {
      if (data.deleteEmployment.success) {
        clearError();
        showSnackbar("Internship deleted");
        onClose();
      } else {
        setError(data.deleteEmployment.message);
      }
    },
    onError: (error) => {
      setError(error.message);
    }
  });

  const handleDelete = async () => {
    await deleteInternship({
      variables: { id: internshipId }
    });
  };

  return (
    <Dialog
      open={open}
      onClosed={onClose}
      headline="Delete Internship"
      actions={
        <div className="flex gap-2">
          <Button variant="text" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="filled"
            theme="error"
            trailingIcon={false}
            icon="delete"
            onClick={handleDelete}
            disabled={loading}
            data-testid="confirm-delete-internship"
          >
            Delete
          </Button>
        </div>
      }
    >
      <div>
        <p>Are you sure you want to delete this internship? This cannot be undone.</p>
        {facilityName && (
          <div className="mt-4">
            <ListItem
              iconName="business_center"
              iconBackground
              headline={facilityName}
              supportingText={formatDateRange(startDate, endDate) || undefined}
            />
          </div>
        )}
      </div>
    </Dialog>
  );
}
